import type { BackendModule, ReadCallback, ResourceKey, Services } from "i18next";
import { logError } from "../logging";

type BackendOptions = {
  loadPath: string;
};

export class I18NextBackend implements BackendModule<BackendOptions> {
  public type = "backend" as const;

  private options = { loadPath: "" };

  init(_services: Services, options: BackendOptions) {
    this.options = { ...this.options, ...options };
  }

  read(lang: string, namespace: string, callback: ReadCallback) {
    void this.readTranslations(lang, namespace, callback);
  }

  private async readTranslations(
    lang: string,
    namespace: string,
    callback: ReadCallback
  ) {
    let translations: ResourceKey | undefined;
    try {
      translations = await this.fetchTranslations(lang, namespace);
    } catch (error) {
      logError(getErrorMessage(error, lang, namespace));
    }

    callback(translations ? null : "Failed to read translations", translations);
  }

  // A plain fetch, so how long a stale bundle can survive a deploy is decided by the
  // caching headers on /locales rather than by anything here.
  async fetchTranslations(lang: string, namespace: string) {
    const url = this.options.loadPath
      .replace("{{lng}}", lang)
      .replace("{{ns}}", namespace);
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(
        `Failed to load UI translations for ${lang}/${namespace}: ` +
          `${response.status} ${response.statusText}`
      );
    }

    const contentType = response.headers.get("Content-Type")?.toLowerCase();
    const responseText = await response.text();
    try {
      return JSON.parse(responseText) as ResourceKey;
    } catch {
      throw new Error(
        `Failed to load UI translations for ${lang}/${namespace}: ` +
          `expected JSON from ${url}, got ${getResponseType(contentType, responseText)}`
      );
    }
  }
}

function getErrorMessage(error: unknown, lang: string, namespace: string) {
  if (error instanceof Error) {
    return error.message;
  }

  return `Failed to load UI translations for ${lang}/${namespace}`;
}

function getResponseType(contentType?: string, responseText?: string) {
  const normalizedResponseText = responseText?.trimStart().toLowerCase() ?? "";
  if (
    contentType?.includes("text/html") ||
    normalizedResponseText.startsWith("<!doctype html") ||
    normalizedResponseText.startsWith("<html")
  ) {
    return "HTML";
  }

  return contentType ?? "non-JSON content";
}
